/**
 * Order Status Badge Component
 *
 * Displays color-coded status badges for orders with appropriate icons.
 * Colors: pending=blue, scheduled=yellow, delivered/in_use=green,
 * pickup_requested/picked_up=purple, completed=gray, cancelled=red
 */

import type { OrderStatus } from '../../types/widget/order';
import { Badge } from '../ui/badge';

interface OrderStatusBadgeProps {
  status: OrderStatus;
  className?: string;
}

interface StatusConfig {
  label: string;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  icon: string;
  className: string;
}

const statusConfig: Record<OrderStatus, StatusConfig> = {
  pending: {
    label: 'Pending',
    variant: 'default',
    icon: '⏱️',
    className: 'bg-blue-100 text-blue-800 hover:bg-blue-200',
  },
  scheduled: {
    label: 'Scheduled',
    variant: 'default',
    icon: '📅',
    className: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',
  },
  delivered: {
    label: 'Delivered',
    variant: 'default',
    icon: '✓',
    className: 'bg-green-100 text-green-800 hover:bg-green-200',
  },
  in_use: {
    label: 'In Use',
    variant: 'default',
    icon: '📦',
    className: 'bg-green-100 text-green-800 hover:bg-green-200',
  },
  pickup_requested: {
    label: 'Pickup Requested',
    variant: 'default',
    icon: '🔔',
    className: 'bg-purple-100 text-purple-800 hover:bg-purple-200',
  },
  picked_up: {
    label: 'Picked Up',
    variant: 'default',
    icon: '🚛',
    className: 'bg-purple-100 text-purple-800 hover:bg-purple-200',
  },
  completed: {
    label: 'Completed',
    variant: 'secondary',
    icon: '✓',
    className: 'bg-gray-100 text-gray-800 hover:bg-gray-200',
  },
  cancelled: {
    label: 'Cancelled',
    variant: 'destructive',
    icon: '✕',
    className: 'bg-red-100 text-red-800 hover:bg-red-200',
  },
};

export function OrderStatusBadge({ status, className = '' }: OrderStatusBadgeProps) {
  const config = statusConfig[status];

  return (
    <Badge className={`${config.className} ${className}`} variant={config.variant}>
      <span className="mr-1">{config.icon}</span>
      {config.label}
    </Badge>
  );
}
